import React, { useEffect, useState } from "react";
import api from "../../api";
import Layout from "../../components/Layout/Layout";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const [outletCount, setOutletCount] = useState(0);
  const [songCount, setSongCount] = useState(0);
  const [playlistCount, setPlaylistCount] = useState(0);
  const navigate = useNavigate();
  const [recentOutlets, setRecentOutlets] = useState([]);
  const [recentSongs, setRecentSongs] = useState([]);
  const [recentPlaylists, setRecentPlaylists] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [status, setStatus] = useState({});

  useEffect(() => {
    const ws = new WebSocket("wss://capimusic.synoweberp.in");

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === "status") {
        setStatus((prev) => {
          const updated = {
            ...prev,
            [data.outletId]: data.online,
          };

          const online = Object.values(updated).filter(Boolean).length;
          setOnlineCount(online);

          return updated;
        });
      }
    };

    return () => ws.close();
  }, []);

  useEffect(() => {
    loadCounts();
  }, []);

  const loadCounts = async () => {
    try {
      const [outletsRes, mediaRes, playlistsRes] = await Promise.all([
        api.get("/api/outlets"),
        api.get("/api/media"),
        api.get("/api/playlist/list"),
      ]);

      // COUNT DATA
      setOutletCount(outletsRes.data.length);
      setSongCount(mediaRes.data.length);
      setPlaylistCount(playlistsRes.data.length);

      // RECENT DATA (last 3 items)
      setRecentOutlets(outletsRes.data.slice(-3).reverse());
      setRecentSongs(mediaRes.data.slice(-3).reverse());
      setRecentPlaylists(playlistsRes.data.slice(-3).reverse());
    } catch (err) {
      console.error("Error loading dashboard counts", err);
    }
  };

  return (
    <div class="px-2 md:px-6 lg:px-10">
      <h1 className="text-3xl font-bold text-gray-800 m-6 text-center">
        Music Dashboard 
      </h1>
      {/* QUICK LINKS */}

      <h3 className="text-xl font-semibold text-gray-700 mb-4">
        Quick Actions
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-10">
        <button
          onClick={() => navigate("/outlets")}
          className="bg-white shadow hover:shadow-lg transition rounded-lg p-4 text-left border"
        >
          <p className="text-lg font-semibold text-gray-700">
            🏪 Manage Outlets
          </p>
          <p className="text-sm text-gray-500">View and manage outlets</p>
        </button>

        <button
          onClick={() => navigate("/Status")}
          className="bg-white shadow hover:shadow-lg transition rounded-lg p-4 text-left border"
        >
          <p className="text-lg font-semibold text-gray-700">🎛 Music Status</p>
          <p className="text-sm text-gray-500">Check outlet playback</p>
        </button>

        <button
          onClick={() => navigate("/playlist")}
          className="bg-white shadow hover:shadow-lg transition rounded-lg p-4 text-left border"
        >
          <p className="text-lg font-semibold text-gray-700">🎵 Playlists</p>
          <p className="text-sm text-gray-500">Create and manage playlists</p>
        </button>

        <button
          onClick={() => navigate("/media")}
          className="bg-white shadow hover:shadow-lg transition rounded-lg p-4 text-left border"
        >
          <p className="text-lg font-semibold text-gray-700">
            🎧 Media Library
          </p>
          <p className="text-sm text-gray-500">Upload and manage songs</p>
        </button>
      </div>

      {/* TOP SUMMARY CARDS */}
      <h3 className="text-xl font-semibold text-gray-700 mb-4">
        System Statistics
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
        {/* OUTLETS */}
        <div className="bg-white shadow-md hover:shadow-xl transition-all rounded-xl p-6 border-l-4 border-blue-600">
          <h3 className="text-lg font-semibold text-gray-700">
            Outlets Registered
          </h3>
          <p className="text-4xl font-bold text-blue-600 mt-2">{outletCount}</p>
          <p className="text-sm text-gray-500 mt-2">Total outlet locations</p>
        </div>
        {/* ONLINE DEVICES */}
        <div className="bg-white shadow-md hover:shadow-xl transition-all rounded-xl p-6 border-l-4 border-emerald-600">
          <h3 className="text-lg font-semibold text-gray-700">
            Online Devices
          </h3>
          <p className="text-4xl font-bold text-emerald-600 mt-2">
            {onlineCount}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Currently connected outlets
          </p>
        </div>

        {/* SONGS */}
        <div className="bg-white shadow-md hover:shadow-xl transition-all rounded-xl p-6 border-l-4 border-purple-600">
          <h3 className="text-lg font-semibold text-gray-700">
            Songs Uploaded
          </h3>
          <p className="text-4xl font-bold text-purple-600 mt-2">{songCount}</p>
          <p className="text-sm text-gray-500 mt-2">Total media files</p>
        </div>
        {/* PLAYLISTS */}
        <div className="bg-white shadow-md hover:shadow-xl transition-all rounded-xl p-6 border-l-4 border-green-600">
          <h3 className="text-lg font-semibold text-gray-700">
            Created Playlists
          </h3>
          <p className="text-4xl font-bold text-green-600 mt-2">
            {playlistCount}
          </p>
          <p className="text-sm text-gray-500 mt-2">Total playlists created</p>
        </div>
      </div>

      {/* RECENT SECTIONS */}
      <h3 className="text-xl font-semibold text-gray-700 mb-4">
        Recent Activity
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* RECENT PLAYLISTS */}
        <div className="bg-white shadow-md rounded-xl p-5">
          <h3 className="text-xl font-semibold text-gray-700 mb-3">
            Recent Playlists
          </h3>

          <ul className="space-y-3">
            {recentPlaylists.length > 0 ? (
              recentPlaylists.map((p) => (
                <li
                  key={p._id}
                  className="p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition"
                >
                  <p className="font-semibold text-gray-700">{p.name}</p>
                  <p className="text-sm text-gray-500">
                    {p.songs?.length || 0} Songs
                  </p>
                </li>
              ))
            ) : (
              <p className="text-gray-500">No playlists found</p>
            )}
          </ul>
        </div>

        {/* RECENT SONGS */}
        <div className="bg-white shadow-md rounded-xl p-5">
          <h3 className="text-xl font-semibold text-gray-700 mb-3">
            Recently Uploaded Songs
          </h3>

          <ul className="space-y-3">
            {recentSongs.length > 0 ? (
              recentSongs.map((s) => (
                <li
                  key={s._id}
                  className="p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition"
                >
                  <p className="font-semibold text-gray-700">
                    {s.title || s.originalName}
                  </p>
                  <p className="text-sm text-gray-500">
                    {s.type?.toUpperCase()} •{" "}
                    {s.size ? (s.size / 1024 / 1024).toFixed(1) : "0"} MB
                  </p>
                </li>
              ))
            ) : (
              <p className="text-gray-500">No songs found</p>
            )}
          </ul>
        </div>

        {/* RECENT OUTLETS */}
        <div className="bg-white shadow-md rounded-xl p-5">
          <h3 className="text-xl font-semibold text-gray-700 mb-3">
            Recently Added Outlets
          </h3>

          <ul className="space-y-3">
            {recentOutlets.length > 0 ? (
              recentOutlets.map((o) => (
                <li
                  key={o._id}
                  className="p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition"
                >
                  <p className="font-semibold text-gray-700">{o.name}</p>
                  <p className="text-sm text-gray-500">{o.city}</p>
                </li>
              ))
            ) : (
              <p className="text-gray-500">No outlets found</p>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
